import { InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { AdminProfileErrorCode, CommonErrorCode } from '~src/commonInfra/utils/ErrorCodes';

import { IUpdateAdminProfileSitesResult } from '../requests/UpdateAdminProfileSitesRequest';
import { AdminProfilesService } from './AdminProfilesService';

export type SiteToRemove = { id: string, name: string };

type RemoveAdminProfileFromSiteParams = {
  profileId: string;
  siteIdsToRemove: string[];
  currentSiteIds: string[];
};

type RemoveAdminProfileFromSiteDialogState = RemoveAdminProfileFromSiteParams & { errorMessage?: string };

type RemoveAdminProfileFromSiteDialogBodyProps = { message: string };

function RemoveAdminProfileFromSiteDialogBody(
  props: ReactActionDialogBodyProps<RemoveAdminProfileFromSiteDialogBodyProps, RemoveAdminProfileFromSiteDialogState>
): JSX.Element {
  return (
    <div>
      {props.message}
      {!!props.dialogState.errorMessage && (
        <InlineAlertWidget testId="remove-admin-profile-from-site-dialog-error" type={InlineAlertType.Error}>
          {props.dialogState.errorMessage}
        </InlineAlertWidget>
      )}
    </div>
  );
}

function formatErrorForDialog(result: IUpdateAdminProfileSitesResult): string {
  const firstError = result.errors?.[0];
  if (!firstError) {
    return Messages.errorUnknown();
  }
  const code = Number(firstError.code);
  switch (code) {
    case AdminProfileErrorCode.AdminProfileNotFound:
      return Messages.adminProfileNotFound();
    case CommonErrorCode.SiteNotFound:
      return Messages.adminProfileSiteNotFound();
    default:
      return firstError.message || Messages.errorUnknown();
  }
}

function showRemoveAdminProfileFromSiteDialog(
  profileId: string,
  profileName: string,
  sitesToRemove: ReadonlyArray<SiteToRemove>,
  currentSiteIds: ReadonlyArray<string>,
  assignedUsersCount: number
): Promise<IUpdateAdminProfileSitesResult> {
  const siteIdsToRemove = sitesToRemove.map((s) => s.id);
  const siteCount = sitesToRemove.length;
  const firstSiteName = sitesToRemove.length > 0 ? sitesToRemove[0].name : '';

  let dialogBody = Messages.confirmRemoveAdminProfileFromSite({
    PROFILE_NAME: L10n.elideIfLong(profileName),
    SITE_COUNT: siteCount,
    SITE_COUNT_LOC: Formatters.formatCount(siteCount),
    SITE_NAME: L10n.elideIfLong(firstSiteName),
  });
  if (assignedUsersCount > 0) {
    dialogBody = `${dialogBody} ${Messages.confirmRemoveAdminProfileFromSiteAssignedUsersWarning()}`;
  }

  const paramsForAction: RemoveAdminProfileFromSiteDialogState = {
    profileId,
    siteIdsToRemove,
    currentSiteIds: Array.from(currentSiteIds),
  };

  const dialogInstance = openReactActionDialog<
    RemoveAdminProfileFromSiteDialogBodyProps,
    RemoveAdminProfileFromSiteDialogState,
    IUpdateAdminProfileSitesResult
  >({
    title: Messages.remove(),
    bodyComponent: RemoveAdminProfileFromSiteDialogBody,
    initialBodyProps: { message: dialogBody },
    initialDialogState: paramsForAction,
    actionName: Messages.confirmRemoveButton({
      ITEM_COUNT: siteCount,
      ITEM_COUNT_LOC: Formatters.formatCount(siteCount),
    }),
    isActionDelete: true,
    dismissDialogOnActionFailed: false,
    action: async (
      _bodyProps,
      { profileId: pId, siteIdsToRemove: toRemove, currentSiteIds: current },
      updateDialogState
    ) => {
      const remainingSiteIds = current.filter((id) => !toRemove.includes(id));
      const result = await AdminProfilesService.updateAdminProfileSites({
        luid: pId,
        siteIds: remainingSiteIds,
      });

      if (result.errors && result.errors.length > 0) {
        updateDialogState({ errorMessage: formatErrorForDialog(result) });
        return Promise.reject(result);
      }

      return result;
    },
    dialogWidth: TabDialogWidth.width375px,
  });

  dialogInstance.result.then((result: IUpdateAdminProfileSitesResult) => {
    if (!result.errors || result.errors.length === 0) {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: Messages.adminProfileSitesUpdatedSuccess(),
      });
    }
  });

  return dialogInstance.result;
}

export const RemoveAdminProfileFromSiteAction = {
  showRemoveAdminProfileFromSiteDialog,
};
